import { useState, useEffect, useRef, useMemo } from 'react'
import { Link, useSearchParams } from 'react-router'
import {
  useProrationFormulas,
  useProrationFormula,
  useCreateProrationFormula,
  useUpdateProrationFormula,
  useCalculateProration,
} from '@/admin/hooks/use-warranties'
import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import { Checkbox } from '@/shared/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/shared/components/ui/select'
import { MasterDetail } from '@/admin/components/shared/master-detail'
import { useTour } from '@/admin/components/tour'
import { Plus, X } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { cn } from '@/shared/lib/utils'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import type {
  FormulaType,
  CoverageScope,
  LinkedWarrantyTemplateSummary,
  ProrationFormula,
  ProrationTier,
  ProrationCalculationResult,
} from '@/admin/types/warranties'

import { toastApiError } from '@/shared/lib/api-error'
import { useFormatters } from '@/shared/lib/formatting'
function useFormulaTypeLabels(): Record<FormulaType, string> {
  const { t } = useTranslation()
  return {
    TIERED: t('prorationFormulas.tiered'),
    LINEAR: t('prorationFormulas.linear'),
    FLAT: t('prorationFormulas.flat'),
  }
}

function useCoverageScopeLabels(): Record<CoverageScope, string> {
  const { t } = useTranslation()
  return {
    MATERIAL_ONLY: t('prorationFormulas.materialOnly'),
    MATERIAL_AND_LABOR: t('prorationFormulas.materialAndLabor'),
  }
}

// ─── Main Page ───────────────────────────────────────────

export function ProrationFormulasPage() {
  const { t } = useTranslation()
  const FORMULA_TYPE_LABELS = useFormulaTypeLabels()
  const COVERAGE_SCOPE_LABELS = useCoverageScopeLabels()
  const { data: formulas, isLoading } = useProrationFormulas()
  const { activeChapter } = useTour()
  // Deep-linkable selection - the warranty template detail page links to
  // a specific formula via ``?formula=<uuid>``. State is derived from the
  // URL to avoid duplicate bookkeeping; ``selectFormula`` only needs to
  // update the search params and React re-renders with the new value.
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedUuid = searchParams.get('formula')

  function selectFormula(uuid: string | null) {
    if (uuid) {
      setSearchParams({ formula: uuid }, { replace: false })
    } else {
      searchParams.delete('formula')
      setSearchParams(searchParams, { replace: true })
    }
  }

  // During the proration tour chapter, auto-select the first formula so the
  // calculator section renders - otherwise the step-3 spotlight has no anchor.
  useEffect(() => {
    if (activeChapter?.id !== 'proration') return
    if (selectedUuid) return
    const first = formulas?.[0]
    if (!first) return
    setSearchParams({ formula: first.uuid }, { replace: true })
  }, [activeChapter, formulas, selectedUuid, setSearchParams])
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const createFormula = useCreateProrationFormula()
  const createFields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'code',
        label: t('prorationFormulas.code'),
        placeholder: t('prorationFormulas.codePlaceholder'),
        value: '',
        autoFocus: true,
        required: 'Code is required',
      },
      {
        type: 'text',
        name: 'name',
        label: t('common.name'),
        placeholder: t('prorationFormulas.namePlaceholder'),
        value: '',
        required: 'Name is required',
      },
      {
        type: 'select',
        name: 'formula_type',
        label: t('prorationFormulas.formulaType'),
        value: 'TIERED',
        options: (Object.keys(FORMULA_TYPE_LABELS) as FormulaType[]).map((formulaType) => ({
          value: formulaType,
          label: FORMULA_TYPE_LABELS[formulaType],
        })),
      },
      {
        type: 'select',
        name: 'coverage_scope',
        label: t('prorationFormulas.coverageScopeLabel'),
        value: 'MATERIAL_AND_LABOR',
        options: (Object.keys(COVERAGE_SCOPE_LABELS) as CoverageScope[]).map((coverageScope) => ({
          value: coverageScope,
          label: COVERAGE_SCOPE_LABELS[coverageScope],
        })),
      },
    ],
    [COVERAGE_SCOPE_LABELS, FORMULA_TYPE_LABELS, t],
  )
  const { Form: CreateFormulaForm, ref: createFormulaFormRef } = useForm(
    {
      name: 'proration-formula-create-form',
      submit: 'footer',
      fields: createFields,
    },
    async (rawValues) => {
      const values = rawValues as {
        code: string
        name: string
        formula_type: FormulaType
        coverage_scope: CoverageScope
      }
      try {
        const created = await createFormula.mutateAsync({
          code: values.code.trim(),
          name: values.name.trim(),
          formula_type: values.formula_type,
          coverage_scope: values.coverage_scope,
        })
        selectFormula(created.uuid)
        setShowCreateDialog(false)
        toast.success(t('prorationFormulas.toasts.created'))
      } catch (err) {
        toastApiError(err, t('prorationFormulas.toasts.createFailed'));
      }
    },
    [showCreateDialog, createFields],
  )

  return (
    <>
      <MasterDetail
        dataTourList="proration-list"
        testId="proration-formulas"
        title={t('prorationFormulas.title')}
        onAdd={() => setShowCreateDialog(true)}
        hasSelection={!!selectedUuid}
        selectedId={selectedUuid}
        visibleItemIds={(formulas ?? []).map((formula) => formula.uuid)}
        onSelectionChange={selectFormula}
        onBack={() => selectFormula(null)}
        list={
          <>
            {isLoading ? (
              <p className="text-[12px] text-[var(--ink-3)]">{t('common.loading')}</p>
            ) : !formulas?.length ? (
              <p className="text-[12px] text-[var(--ink-3)]">{t('prorationFormulas.emptyState')}</p>
            ) : (
              <div className="flex flex-col gap-0.5">
                {formulas.map((f) => {
                  const isActive = f.uuid === selectedUuid
                  return (
                    <Button
                      key={f.uuid}
                      variant="ghost"
                      onClick={() => selectFormula(f.uuid)}
                      data-testid={`proration-formulas-item-${f.uuid}`}
                      className={cn(
                        'h-auto w-full justify-start text-left px-3 py-2 rounded-[4px] text-[12px] transition-all duration-150 cursor-pointer',
                        isActive
                          ? 'text-[var(--green)] bg-[var(--green-bg-subtle)]'
                          : 'text-[var(--ink-2)] bg-transparent hover:bg-[var(--bg-subtle)]',
                      )}
                    >
                      <div className="flex flex-col">
                        <div className="flex items-center gap-2">
                          <span className="font-[400]">{f.code}</span>
                          {f.is_active && (
                            <span
                              className="inline-block h-1.5 w-1.5 rounded-full shrink-0 bg-[var(--green)]"
                            />
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[11px] text-[var(--ink-3)]">{f.name}</span>
                          <TypeBadge>{FORMULA_TYPE_LABELS[f.formula_type]}</TypeBadge>
                        </div>
                      </div>
                    </Button>
                  );
                })}
              </div>
            )}
          </>
        }
        detail={
          selectedUuid ? (
            <FormulaDetail formulaUuid={selectedUuid} />
          ) : (
            <div className="flex justify-center pt-24">
              <p className="text-[13px] text-[var(--ink-3)]">
                {t('prorationFormulas.selectFormula')}
              </p>
            </div>
          )
        }
      />
      {/* Create formula dialog */}
      <Dialog
        open={showCreateDialog}
        onOpenChange={(open) => {
          setShowCreateDialog(open)
          if (!open) createFormulaFormRef.current?.resetForm()
        }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('prorationFormulas.newFormula')}</DialogTitle>
          </DialogHeader>
          <CreateFormulaForm className="flex flex-col gap-4 py-2" />
          <DialogFooter>
            <GreenButton
              onClick={() => createFormulaFormRef.current?.submitForm()}
              disabled={createFormula.isPending}
              data-testid="proration-formulas-dialog-submit"
            >
              {t('common.create')}
            </GreenButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Formula Detail ──────────────────────────────────────

function FormulaDetail({ formulaUuid }: { formulaUuid: string }) {
  const { t } = useTranslation()
  const { formatDateTime } = useFormatters()
  const FORMULA_TYPE_LABELS = useFormulaTypeLabels()
  const COVERAGE_SCOPE_LABELS = useCoverageScopeLabels()
  const { data: formula, isLoading } = useProrationFormula(formulaUuid)
  const updateFormula = useUpdateProrationFormula()

  // Local editable state
  const [editCode, setEditCode] = useState('')
  const [editName, setEditName] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editCoverageScope, setEditCoverageScope] = useState<CoverageScope>('MATERIAL_AND_LABOR')
  const [editIsActive, setEditIsActive] = useState(true)
  const [editTiers, setEditTiers] = useState<ProrationTier[]>([])
  const [editWarrantyPeriodYears, setEditWarrantyPeriodYears] = useState<number>(5)
  const [isDirty, setIsDirty] = useState(false)

  function syncFromFormula(f: ProrationFormula) {
    setEditCode(f.code)
    setEditName(f.name)
    setEditDescription(f.description || '')
    setEditCoverageScope(f.coverage_scope)
    setEditIsActive(f.is_active)
    setEditTiers(f.schedule?.tiers ? [...f.schedule.tiers].sort((a, b) => a.year_start - b.year_start) : [])
    setEditWarrantyPeriodYears(f.schedule?.warranty_period_years ?? 5)
    setIsDirty(false)
  }

  // Sync local state when formula loads or uuid changes
  const prevUuidRef = useRef<string | null>(null)
  useEffect(() => {
    if (formula && prevUuidRef.current !== formulaUuid) {
      prevUuidRef.current = formulaUuid
       
      syncFromFormula(formula)
    }
  }, [formula, formulaUuid])

  function markDirty() { setIsDirty(true) }

  async function handleSave() {
    if (!formula) return
    const schedule =
      formula.formula_type === 'LINEAR'
        ? { type: 'linear', warranty_period_years: editWarrantyPeriodYears }
        : { type: formula.formula_type.toLowerCase(), tiers: editTiers }

    try {
      await updateFormula.mutateAsync({
        uuid: formula.uuid,
        data: {
          name: editName,
          description: editDescription,
          coverage_scope: editCoverageScope,
          is_active: editIsActive,
          schedule,
        },
      })
      setIsDirty(false)
      toast.success(t('prorationFormulas.toasts.saved'))
    } catch (err) {
      toastApiError(err, t('prorationFormulas.toasts.saveFailed'));
    }
  }

  // Tier management
  function addTier() {
    const lastTier = editTiers[editTiers.length - 1]
    const nextStart = lastTier ? lastTier.year_end : 0
    setEditTiers([...editTiers, { year_start: nextStart, year_end: nextStart + 1, material_pct: 100, labor_pct: 100 }])
    markDirty()
  }

  function updateTier(index: number, field: keyof ProrationTier, value: number) {
    const updated = editTiers.map((tier, i) => (i === index ? { ...tier, [field]: value } : tier))
    setEditTiers(updated.sort((a, b) => a.year_start - b.year_start))
    markDirty()
  }

  function removeTier(index: number) {
    setEditTiers(editTiers.filter((_, i) => i !== index))
    markDirty()
  }

  if (isLoading) {
    return <p className="text-[13px] text-[var(--ink-3)]">{t('common.loading')}</p>;
  }

  if (!formula) return null

  const isExisting = true // formula already exists, so code + formula_type are read-only

  return (
    <div className="relative pb-16">
      {/* Header: Code as title */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <span className="font-display text-[20px] text-[var(--ink)]">
            {editCode}
          </span>
          <TypeBadge>{FORMULA_TYPE_LABELS[formula.formula_type]}</TypeBadge>
        </div>
      </div>
      {/* Section 1: Core Details */}
      <SectionLabel>{t('prorationFormulas.coreDetails')}</SectionLabel>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <Label className="clara-section-label mb-1.5 block">{t('prorationFormulas.code')}</Label>
          <Input
            value={editCode}
            onChange={(e) => { setEditCode(e.target.value); markDirty() }}
            disabled={isExisting}
            className="text-[13px]"
          />
        </div>
        <div>
          <Label className="clara-section-label mb-1.5 block">{t('common.name')}</Label>
          <Input
            value={editName}
            onChange={(e) => { setEditName(e.target.value); markDirty() }}
            className="text-[13px]"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <Label className="clara-section-label mb-1.5 block">{t('prorationFormulas.formulaType')}</Label>
          <Select value={formula.formula_type} disabled>
            <SelectTrigger size="sm" className="text-[11px] w-full">
              <SelectValue>{FORMULA_TYPE_LABELS[formula.formula_type]}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(FORMULA_TYPE_LABELS) as FormulaType[]).map((ft) => (
                <SelectItem key={ft} value={ft}>{FORMULA_TYPE_LABELS[ft]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="clara-section-label mb-1.5 block">{t('prorationFormulas.coverageScopeLabel')}</Label>
          <Select value={editCoverageScope} onValueChange={(v) => { setEditCoverageScope(v as CoverageScope); markDirty() }}>
            <SelectTrigger size="sm" className="text-[11px] w-full">
              <SelectValue>{COVERAGE_SCOPE_LABELS[editCoverageScope]}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(COVERAGE_SCOPE_LABELS) as CoverageScope[]).map((cs) => (
                <SelectItem key={cs} value={cs}>{COVERAGE_SCOPE_LABELS[cs]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="mb-4">
        <Label className="clara-section-label mb-1.5 block">{t('common.description')}</Label>
        <textarea
          value={editDescription}
          onChange={(e) => { setEditDescription(e.target.value); markDirty() }}
          placeholder={t('prorationFormulas.descriptionPlaceholder')}
          className="min-h-[72px] w-full resize-none rounded-[4px] border-[0.5px] border-[var(--rule-strong)] bg-transparent px-3 py-2 text-[13px] text-[var(--ink)] outline-none"
        />
      </div>
      <Label className="flex items-center gap-2.5 cursor-pointer mb-6">
        <Checkbox
          checked={editIsActive}
          onCheckedChange={(checked) => { setEditIsActive(checked as boolean); markDirty() }}
          className="accent-[var(--green)] cursor-pointer"
        />
        <span className="text-[13px] text-[var(--ink-2)]">
          {t('prorationFormulas.active')}
        </span>
      </Label>
      {/* Section 2: Schedule Configuration */}
      <div className="mb-6 border-t-[0.5px] border-t-[var(--rule)] pt-6">
        <SectionLabel>{t('prorationFormulas.scheduleConfiguration')}</SectionLabel>

        {formula.formula_type === 'LINEAR' ? (
          <LinearSchedule
            warrantyPeriodYears={editWarrantyPeriodYears}
            onChange={(v) => { setEditWarrantyPeriodYears(v); markDirty() }}
          />
        ) : (
          <TieredSchedule
            tiers={editTiers}
            coverageScope={editCoverageScope}
            onAddTier={addTier}
            onUpdateTier={updateTier}
            onRemoveTier={removeTier}
          />
        )}
      </div>
      {/* Section 3: Calculator */}
      <div data-tour="proration-calculator" className="mb-6 border-t-[0.5px] border-t-[var(--rule)] pt-6">
        <SectionLabel>{t('prorationFormulas.calculator')}</SectionLabel>
        <Calculator
          formulaUuid={formula.uuid}
          coverageScope={editCoverageScope}
        />
      </div>
      {/* Section 4: Used by warranty templates */}
      <div className="mb-6 border-t-[0.5px] border-t-[var(--rule)] pt-6">
        <LinkedWarrantyTemplates
          templates={formula.linked_warranty_templates ?? []}
        />
      </div>
      {/* Section 5: Metadata */}
      <div className="border-t-[0.5px] border-t-[var(--rule)] pt-6">
        <SectionLabel>{t('prorationFormulas.metadata')}</SectionLabel>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="clara-section-label mb-1.5 block">{t('prorationFormulas.created')}</Label>
            <span className="text-[12px] text-[var(--ink-3)]">
              {formula.created_at ? formatDateTime(formula.created_at) : '-'}
            </span>
          </div>
          <div>
            <Label className="clara-section-label mb-1.5 block">{t('prorationFormulas.updated')}</Label>
            <span className="text-[12px] text-[var(--ink-3)]">
              {formula.updated_at ? formatDateTime(formula.updated_at) : '-'}
            </span>
          </div>
        </div>
      </div>
      {/* Sticky action buttons */}
      {isDirty && (
        <div className="fixed bottom-6 right-8 z-10 flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => syncFromFormula(formula)}
            disabled={updateFormula.isPending}
            className="cursor-pointer rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-4 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-2)] shadow-[var(--shadow-float)] transition-all duration-150 hover:bg-[var(--bg-subtle)]"
          >
            {t('common.cancel')}
          </Button>
          <GreenButton onClick={handleSave} disabled={updateFormula.isPending}>
            {t('common.save')}
          </GreenButton>
        </div>
      )}
    </div>
  );
}

// ─── Used by warranty templates ──────────────────────────

function LinkedWarrantyTemplates({
  templates,
}: {
  templates: LinkedWarrantyTemplateSummary[]
}) {
  const { t } = useTranslation()
  const title = t('prorationFormulas.usedByTemplates', {
    defaultValue: 'Used by warranty templates',
  })
  const empty = t('prorationFormulas.usedByTemplatesEmpty', {
    defaultValue: 'No warranty templates reference this formula yet.',
  })
  const count = templates.length

  return (
    <div>
      <div className="mb-3 flex items-center gap-2">
        <SectionLabel>{title}</SectionLabel>
        <span className="rounded-full bg-[var(--green-bg)] px-2 py-0.5 text-[10px] uppercase tracking-[0.1em] text-[var(--green)]">
          {count}
        </span>
      </div>
      {count === 0 ? (
        <p className="text-[12px] text-[var(--ink-3)]">{empty}</p>
      ) : (
        <ul className="flex flex-col gap-1 rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-1">
          {templates.map((tpl) => (
            <li key={tpl.uuid}>
              <Link
                to={`/manage/warranties/templates/${tpl.uuid}`}
                className="flex items-center justify-between gap-3 rounded-[4px] px-3 py-2 text-[13px] text-[var(--ink)] transition-colors hover:bg-[var(--bg-subtle)]"
              >
                <span className="flex items-center gap-2 truncate">
                  <span className="truncate">{tpl.name}</span>
                  {tpl.policy_code && (
                    <span className="text-[11px] text-[var(--ink-3)]">
                      {tpl.policy_code}
                    </span>
                  )}
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  <span className="rounded-full bg-[var(--green-bg)] px-2 py-0.5 text-[9px] uppercase tracking-[0.1em] text-[var(--green)]">
                    {tpl.warranty_type.replace(/_WARRANTY$/i, '').replace(/_/g, ' ')}
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                    {tpl.status}
                  </span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ─── Tiered / Flat Schedule ──────────────────────────────

function TieredSchedule({
  tiers,
  coverageScope,
  onAddTier,
  onUpdateTier,
  onRemoveTier,
}: {
  tiers: ProrationTier[]
  coverageScope: CoverageScope
  onAddTier: () => void
  onUpdateTier: (index: number, field: keyof ProrationTier, value: number) => void
  onRemoveTier: (index: number) => void
}) {
  const { t } = useTranslation()
  const showLabor = coverageScope === 'MATERIAL_AND_LABOR'

  return (
    <div>
      <div className="overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule)]">
        {/* Table header */}
        <div
          className={cn(
            'grid gap-2 border-b-[0.5px] border-b-[var(--rule)] bg-[var(--bg-subtle)] px-4 py-2',
            showLabor ? 'grid-cols-[1fr_1fr_1fr_1fr_32px]' : 'grid-cols-[1fr_1fr_1fr_32px]',
          )}
        >
          <span className="clara-th">{t('prorationFormulas.yearStart')}</span>
          <span className="clara-th">{t('prorationFormulas.yearEnd')}</span>
          <span className="clara-th">{t('prorationFormulas.materialPct')}</span>
          {showLabor && <span className="clara-th">{t('prorationFormulas.laborPct')}</span>}
          <span />
        </div>

        {/* Rows */}
        {tiers.length === 0 ? (
          <div className="px-4 py-4 text-center">
            <p className="text-[12px] text-[var(--ink-3)]">
              {t('prorationFormulas.noTiers')}
            </p>
          </div>
        ) : (
          tiers.map((tier, idx) => (
            <div
              key={idx}
              className={cn(
                'group grid items-center gap-2 px-4 py-2 transition-all duration-100 hover:bg-[var(--bg-subtle)]',
                showLabor ? 'grid-cols-[1fr_1fr_1fr_1fr_32px]' : 'grid-cols-[1fr_1fr_1fr_32px]',
                idx < tiers.length - 1 && 'border-b-[0.5px] border-b-[var(--rule)]',
              )}
            >
              <Input
                type="text" inputMode="numeric"
                value={tier.year_start}
                onChange={(e) => onUpdateTier(idx, 'year_start', Number(e.target.value))}
                className="w-full rounded-[4px] border-[0.5px] border-[var(--rule-strong)] bg-transparent px-2 py-1 text-[13px] text-[var(--ink)] outline-none"
                step={1}
                min={0}
              />
              <Input
                type="text" inputMode="numeric"
                value={tier.year_end}
                onChange={(e) => onUpdateTier(idx, 'year_end', Number(e.target.value))}
                className="w-full rounded-[4px] border-[0.5px] border-[var(--rule-strong)] bg-transparent px-2 py-1 text-[13px] text-[var(--ink)] outline-none"
                step={1}
                min={0}
              />
              <Input
                type="text" inputMode="numeric"
                value={tier.material_pct}
                onChange={(e) => onUpdateTier(idx, 'material_pct', Number(e.target.value))}
                className="w-full rounded-[4px] border-[0.5px] border-[var(--rule-strong)] bg-transparent px-2 py-1 text-[13px] text-[var(--ink)] outline-none"
                step={1}
                min={0}
                max={100}
              />
              {showLabor && (
                <Input
                  type="text" inputMode="numeric"
                  value={tier.labor_pct}
                  onChange={(e) => onUpdateTier(idx, 'labor_pct', Number(e.target.value))}
                  className="w-full rounded-[4px] border-[0.5px] border-[var(--rule-strong)] bg-transparent px-2 py-1 text-[13px] text-[var(--ink)] outline-none"
                  step={1}
                  min={0}
                  max={100}
                />
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onRemoveTier(idx)}
                className="h-6 w-6 cursor-pointer items-center justify-center rounded-[4px] text-[var(--ink-3)] opacity-0 transition-all duration-150 group-hover:opacity-100 hover:bg-[var(--bg-subtle)]"
                title={t('prorationFormulas.removeTier')}
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          ))
        )}
      </div>
      <Button
        variant="ghost"
        onClick={onAddTier}
        className="mt-3 flex h-auto cursor-pointer items-center gap-1.5 rounded-[4px] px-3 py-1.5 text-[11px] text-[var(--ink-3)] transition-all duration-150 hover:bg-[var(--bg-subtle)]"
      >
        <Plus className="h-3 w-3" />
        {t('prorationFormulas.addTier')}
      </Button>
    </div>
  );
}

// ─── Linear Schedule ─────────────────────────────────────

function LinearSchedule({
  warrantyPeriodYears,
  onChange,
}: {
  warrantyPeriodYears: number
  onChange: (v: number) => void
}) {
  const { t } = useTranslation()
  return (
    <div>
      <div className="mb-4">
        <Label className="clara-section-label mb-1.5 block">{t('prorationFormulas.warrantyPeriodYears')}</Label>
        <Input
          type="text" inputMode="numeric"
          value={warrantyPeriodYears}
          onChange={(e) => onChange(Number(e.target.value))}
          className="text-[13px] max-w-[200px]"
          step={1}
          min={1}
        />
      </div>
      <div className="rounded-[4px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-4 py-3">
        <p className="text-[12px] text-[var(--ink-2)]">
          Coverage = (1 - years_used / {warrantyPeriodYears}) &times; cost
        </p>
      </div>
    </div>
  );
}

// ─── Calculator ──────────────────────────────────────────

function Calculator({
  formulaUuid,
  coverageScope,
}: {
  formulaUuid: string
  coverageScope: CoverageScope
}) {
  const { t } = useTranslation()
  const [yearsElapsed, setYearsElapsed] = useState<number>(0)
  const [materialCost, setMaterialCost] = useState<number>(0)
  const [laborCost, setLaborCost] = useState<number>(0)
  const [result, setResult] = useState<ProrationCalculationResult | null>(null)
  const calculate = useCalculateProration()

  const isMaterialOnly = coverageScope === 'MATERIAL_ONLY'

  async function handleCalculate() {
    try {
      const res = await calculate.mutateAsync({
        formula_uuid: formulaUuid,
        years_elapsed: yearsElapsed,
        material_cost: materialCost,
        labor_cost: isMaterialOnly ? 0 : laborCost,
      })
      setResult(res)
    } catch (err) {
      toastApiError(err, t('prorationFormulas.toasts.calculationFailed'));
      setResult(null)
    }
  }

  return (
    <div>
      <div className="grid grid-cols-3 gap-4 mb-4">
        <div>
          <Label className="clara-section-label mb-1.5 block">{t('prorationFormulas.yearsElapsed')}</Label>
          <Input
            type="text" inputMode="numeric"
            value={yearsElapsed}
            onChange={(e) => setYearsElapsed(Number(e.target.value))}
            className="text-[13px]"
            step={0.5}
            min={0}
          />
        </div>
        <div>
          <Label className="clara-section-label mb-1.5 block">{t('prorationFormulas.materialCost')}</Label>
          <Input
            type="text" inputMode="numeric"
            value={materialCost}
            onChange={(e) => setMaterialCost(Number(e.target.value))}
            className="text-[13px]"
            step={0.01}
            min={0}
          />
        </div>
        <div>
          <Label className="clara-section-label mb-1.5 block">{t('prorationFormulas.laborCost')}</Label>
          <Input
            type="text" inputMode="numeric"
            value={laborCost}
            onChange={(e) => setLaborCost(Number(e.target.value))}
            className="text-[13px]"
            step={0.01}
            min={0}
            disabled={isMaterialOnly}
          />
        </div>
      </div>
      <Button
        variant="outline"
        onClick={handleCalculate}
        disabled={calculate.isPending}
        className="mb-4 h-auto cursor-pointer rounded-[4px] border-[0.5px] border-[var(--rule-strong)] px-4 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-2)] transition-all duration-150 hover:bg-[var(--bg-subtle)] disabled:opacity-40"
      >
        {calculate.isPending ? t('prorationFormulas.calculating') : t('prorationFormulas.calculate')}
      </Button>
      {result && (
        <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-5 py-4">
          <span className="clara-section-label block mb-3">{t('prorationFormulas.result')}</span>
          <div className="grid grid-cols-2 gap-x-8 gap-y-2">
            <ResultRow label={t('prorationFormulas.materialCoverage')} value={`${result.material_pct}%`} />
            <ResultRow label={t('prorationFormulas.laborCoverage')} value={`${result.labor_pct}%`} />
            <ResultRow label={t('prorationFormulas.materialAmount')} value={`$${result.material_amount}`} />
            <ResultRow label={t('prorationFormulas.laborAmount')} value={`$${result.labor_amount}`} />
          </div>
          <div className="mt-3 flex items-center justify-between border-t-[0.5px] border-t-[var(--rule)] pt-3">
            <span className="text-[12px] font-[400] text-[var(--ink)]">{t('prorationFormulas.total')}</span>
            <span className="text-[14px] font-[400] text-[var(--green)]">
              ${result.total_amount}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function ResultRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[12px] text-[var(--ink-3)]">{label}</span>
      <span className="text-[13px] text-[var(--ink)]">{value}</span>
    </div>
  );
}

// ─── Shared UI helpers ───────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <span className="clara-section-label block mb-4">{children}</span>
  )
}

function GreenButton({
  children,
  onClick,
  disabled,
  'data-testid': dataTestId,
}: {
  children: React.ReactNode
  onClick: () => void
  disabled?: boolean
  'data-testid'?: string
}) {
  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      data-testid={dataTestId}
      className="h-auto cursor-pointer rounded-[4px] bg-[var(--green)] px-4 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] transition-all duration-150 hover:bg-[var(--green-hover)] disabled:opacity-40"
    >
      {children}
    </Button>
  )
}

function TypeBadge({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full bg-[var(--bg-subtle)] px-2 py-0.5 text-[10px] uppercase tracking-[0.06em] text-[var(--ink-3)]">
      {children}
    </span>
  );
}
